import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { List, ListItem, ListItemButton, ListItemIcon, ListItemText, IconButton, useMediaQuery, useTheme, Drawer } from "@mui/material";
import { motion } from "framer-motion";
// Import icons from react-icons libraries
import { FiHome, FiCompass, FiVideo, FiBookmark, FiSettings, FiUser, FiMenu, FiX } from "react-icons/fi";
import { HiOutlineSparkles } from "react-icons/hi";
import { MdOutlineOndemandVideo } from "react-icons/md";
import { RiUserStarLine } from "react-icons/ri";
import { BsBookmarkHeart } from "react-icons/bs";
import { IoSettingsOutline } from "react-icons/io5";

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isSmallScreen = useMediaQuery('(max-width:400px)');
  const isVerySmallScreen = useMediaQuery('(max-width:320px)');
  
  // Set initial state based on screen size
  const [open, setOpen] = useState(!isMobile);
  const user = useSelector((state) => state.user);
  const _id = user?._id;

  // Close sidebar automatically on mobile when screen size changes
  useEffect(() => {
    if (isMobile) {
      setOpen(false);
    } else {
      setOpen(true);
    }
  }, [isMobile]);

  // Define your menu items with updated icons and routes
  const menuItems = [
    { text: "Home", icon: <FiHome size={isVerySmallScreen ? 16 : isSmallScreen ? 18 : 22} />, route: "" },
    // { text: "Discover Talents", icon: <HiOutlineSparkles size={22} />, route: "/discover" },
    { text: "My Uploads", icon: <MdOutlineOndemandVideo size={isVerySmallScreen ? 16 : isSmallScreen ? 18 : 22} />, route: `/uploads/${_id}` },
    { text: "Saved", icon: <BsBookmarkHeart size={isVerySmallScreen ? 16 : isSmallScreen ? 18 : 22} />, route: `/saved/${_id}` },
    // { text: "Settings", icon: <IoSettingsOutline size={22} />, route: "/settings" },
    { text: "Profile", icon: <RiUserStarLine size={isVerySmallScreen ? 16 : isSmallScreen ? 18 : 22} />, route: `/profile/${_id}` },
  ];

  // Update active state based on the current URL
  const [active, setActive] = useState("Home");

  useEffect(() => {
    // Extract the base path without user ID to determine active menu item
    const path = location.pathname.replace("/home", "");
    const basePath = path.split('/').slice(0, 2).join('/');
    
    // Find the menu item that matches the current path
    const currentMenuItem = menuItems.find(item => {
      // For paths with IDs (like /saved/123), we compare only the base path (/saved)
      const itemBasePath = item.route.split('/').slice(0, 2).join('/');
      return basePath === itemBasePath || path === item.route;
    });
    
    setActive(currentMenuItem?.text || "Home");
  }, [location, menuItems]);

  // Calculate sidebar width based on screen size
  const getSidebarWidth = () => {
    if (isVerySmallScreen) return "150px";
    if (isSmallScreen) return "160px";
    if (isMobile) return "180px";
    if (isTablet) return "180px";
    return "200px";
  };

  const sidebarWidth = getSidebarWidth();

  // Drawer content to be used in both mobile and desktop views
  const sidebarContent = (
    <List sx={{ 
      paddingTop: { xs: "40px", sm: "60px" }, 
      display: "flex", 
      flexDirection: "column", 
      justifyContent: "center", 
      height: "90vh",
      width: "100%"
    }}>
      {menuItems.map(({ text, icon, route }) => (
        <ListItem 
          disablePadding 
          key={text} 
          sx={{ 
            px: isVerySmallScreen ? 0.5 : isSmallScreen ? 1 : 2 
          }}
        >
          <ListItemButton
            onClick={() => {
              setActive(text);
              navigate(`/home${route}`);
              if (isMobile) setOpen(false); // Close drawer after click on mobile
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              borderRadius: isVerySmallScreen ? "16px" : "20px",
              padding: isVerySmallScreen ? "6px 8px" : isSmallScreen ? "8px 12px" : "12px 18px",
              marginBottom: isVerySmallScreen ? "4px" : isSmallScreen ? "6px" : "10px",
              color: active === text ? "#fff" : "#333",
              background: active === text
                ? "linear-gradient(to right, #ff758c, #ff7eb3)"
                : "transparent",
              "&:hover": {
                background: "linear-gradient(to right, #ff758c, #ff7eb3)", 
                borderRadius: isVerySmallScreen ? "16px" : "20px",
              },
              transition: "all 0.3s ease-in-out",
            }}
          >
            <ListItemIcon 
              sx={{ 
                color: active === text ? "#fff" : "#666",
                minWidth: isVerySmallScreen ? "24px" : isSmallScreen ? "28px" : "40px" // Even smaller icon spacing
              }}
            >
              {icon}
            </ListItemIcon>
            <ListItemText 
              primary={text} 
              sx={{ 
                opacity: 1,
                "& .MuiTypography-root": {
                  fontSize: isVerySmallScreen ? "0.75rem" : isSmallScreen ? "0.85rem" : "1rem" // Even smaller text
                }
              }} 
            />
          </ListItemButton>
        </ListItem>
      ))}
    </List>
  );

  return (
    <>
      {/* Menu toggle button - always visible */}
      <IconButton 
        onClick={() => setOpen(!open)} 
        sx={{ 
          position: "fixed",
          top: isVerySmallScreen ? 6 : isSmallScreen ? 10 : 20, 
          left: isVerySmallScreen ? 6 : isSmallScreen ? 10 : 20, 
          zIndex: 1300, 
          color: "#555555",
          fontSize: isVerySmallScreen ? "24px" : isSmallScreen ? "28px" : "32px", 
          padding: isVerySmallScreen ? "2px" : isSmallScreen ? "4px" : "8px",
          "& svg": { fontSize: isVerySmallScreen ? "18px" : isSmallScreen ? "20px" : "25px" },
        }}
      >
        {open && isMobile ? <FiX size={isVerySmallScreen ? 18 : isSmallScreen ? 20 : 24} /> : <FiMenu size={isVerySmallScreen ? 18 : isSmallScreen ? 20 : 24} />}
      </IconButton>

      {/* Mobile: Use drawer component that slides in */}
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={open}
          onClose={() => setOpen(false)}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          sx={{
            '& .MuiDrawer-paper': {
              width: sidebarWidth,
              boxSizing: 'border-box',
              boxShadow: "2px 0px 5px rgba(0,0,0,0.1)",
            },
          }}
        >
          {sidebarContent}
        </Drawer>
      ) : (
        /* Desktop: Use animated div for persistent sidebar */
        <motion.div
          initial={{ x: open ? 0 : -parseInt(sidebarWidth) }}
          animate={{ x: open ? 0 : -parseInt(sidebarWidth) }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          style={{
            width: sidebarWidth,
            position: "fixed",
            top: 0,
            left: 0,
            height: "100vh",
            backgroundColor: "#fff",
            boxShadow: "2px 0px 5px rgba(0,0,0,0.1)",
            overflowX: "hidden",
            zIndex: 1200,
          }}
        >
          {sidebarContent}
        </motion.div>
      )}
    </>
  );
};

export default Sidebar;